import React, { useState } from 'react';
import { useQuery } from 'core/hooks';
import { actualiteService, tagService } from 'core/services';
import { PageLoader, Modal, SGSButton, SGSCard, SGSBadge, SGSInput, SGSSelect, SGSHelpButton } from 'components';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Typography } from '@material-ui/core';
import { Article, ArticleMode, ArticleModeRouteNames, DecryptProp } from 'core/models';
import { byDateDesc, bySortOrder } from 'core/utils';
import moment from 'moment';
import styles from '../Actualites.module.scss';
import { useAppContext } from 'core/contexts';
import { isAllowedToManagContent } from 'core/utils/roles';

type Props = {
    mode: ArticleMode;
};

const ActualitesList: React.FC<Props> = ({ mode }) => {
    const [filters, setFilters] = useState<actualiteService.ArticleFilters>();
    const { data, loading } = useQuery(actualiteService.getRubriquesWithArticles, mode, filters);
    const { data: tags } = useQuery(tagService.getTags);
    const { user } = useAppContext();

    const history = useHistory();
    const { t } = useTranslation();

    const [dialogArticle, setDialogArticle] = useState<Article>();
    const [dialogOpened, setDialogOpened] = useState(false);

    const [searchValue, setSearchValue] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);

    const search = () => {
        setFilters({
            search: searchValue,
            tags: selectedTags.filter((t) => t), // On supprime les valeurs non définies...
        });
    };

    function openDialog(article: Article) {
        setDialogArticle(article);
        setDialogOpened(true);
    }

    function handleDialogClose() {
        setDialogOpened(false);
    }

    return (
        <div>
            <SGSHelpButton />
            <Box display="flex" justifyContent="center" alignItems="center" className="mb-4">
                <SGSSelect
                    label={t('help.faq.question.tag')}
                    multiple
                    options={
                        tags?.map((o) => ({
                            value: o.id.toString(),
                            label: o.name,
                        })) ?? []
                    }
                    value={selectedTags}
                    onChange={(e) => setSelectedTags(e.target.value as string[])}
                    className={styles.tagSelect}
                />
                <SGSInput className="mx-2" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} label={t('global.searchTerm')} onKeyDown={(e) => e.key === 'Enter' && search()} />
                <SGSButton onClick={() => search()}>{t('global.search')}</SGSButton>
            </Box>
            {isAllowedToManagContent(user) && (
                <div className="mb-4">
                    <SGSButton
                        className="mr-2"
                        onClick={() => {
                            history.push(`/${ArticleModeRouteNames[mode]}/articles`);
                        }}
                    >
                        {t('article.admin')}
                    </SGSButton>
                    <SGSButton
                        className="mr-2"
                        onClick={() => {
                            history.push(`/${ArticleModeRouteNames[mode]}/rubriques`);
                        }}
                    >
                        {t('rubrique.admin')}
                    </SGSButton>
                    <SGSButton
                        onClick={() => {
                            history.push(`/${ArticleModeRouteNames[mode]}/tags`);
                        }}
                    >
                        {t('tag.admin')}
                    </SGSButton>
                </div>
            )}
            <PageLoader loading={loading}>
                {!loading &&
                    data?.map((rubrique) => (
                        <div key={rubrique.id} className="mb-4">
                            <Typography variant="h1" component="h2" color="primary" gutterBottom>
                                {rubrique.name}
                            </Typography>
                            <Grid container spacing={2}>
                                {rubrique.articles
                                    ?.sort((a, b) => (mode === 'articles' ? byDateDesc(a.article, b.article) : bySortOrder((a as DecryptProp).article, (b as DecryptProp).article)))
                                    .map((rubriqueArticle) => (
                                        <Grid key={rubriqueArticle.article.id} item xs={12} md={6} lg={3}>
                                            <SGSCard
                                                onClick={() =>
                                                    rubriqueArticle.article.isBreve ? openDialog(rubriqueArticle) : history.push(`/${ArticleModeRouteNames[mode]}/${rubriqueArticle.article.slug}`)
                                                }
                                                // @ts-ignore
                                                id={rubriqueArticle.article.slug} // Pour faire fonctionner plus tard la sélection de l'article depuis l'url
                                                color="default"
                                                footer={
                                                    rubriqueArticle.tags.length > 0 && (
                                                        <Grid container justifyContent="flex-end">
                                                            {rubriqueArticle.tags.map((o) => (
                                                                <Grid item key={o.id}>
                                                                    <SGSBadge color="darkorange">{o.name}</SGSBadge>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    )
                                                }
                                                title={rubriqueArticle.article.title}
                                                noTitleTranslation
                                                className="w-100"
                                                image={rubriqueArticle.article.thumbnailImagePath}
                                            >
                                                {mode === 'articles' && rubriqueArticle.article.createdAt && (
                                                    <Box className={styles.articleDate}>
                                                        <em>{moment(rubriqueArticle.article.createdAt).format('L')}</em>
                                                    </Box>
                                                )}
                                                <Box>{rubriqueArticle.article.incipit}</Box>
                                            </SGSCard>
                                        </Grid>
                                    ))}
                            </Grid>
                        </div>
                    ))}
                <Modal
                    setIsOpenModal={handleDialogClose}
                    // footer={
                    //     dialogArticle?.tags.length > 0 && (
                    //         <Grid container justifyContent="flex-end">
                    //             {dialogArticle.tags.map((o) => (
                    //                 <Grid item key={o.id}>
                    //                     <SGSBadge color="darkorange">{o.name}</SGSBadge>
                    //                 </Grid>
                    //             ))}
                    //         </Grid>
                    //     )
                    // }
                    title={dialogArticle?.article?.title}
                    isOpenModal={dialogOpened}
                >
                    <div className={styles.articleContent} dangerouslySetInnerHTML={{ __html: dialogArticle?.article?.content }}></div>
                </Modal>
            </PageLoader>
        </div>
    );
};

export default ActualitesList;
